// Import the RTK Query methods from the React-specific entry point
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { Auth } from "aws-amplify";
import { IMessage, IStreamList } from "api/apiTypes";
import {Buffer} from 'buffer';


// Define our single API slice object
export const apiSlice = createApi({
  // The cache reducer expects to be added at `state.api` (already default - this is optional)
  reducerPath: "api",
  // All of our requests will have URLs starting with '/fakeApi'
  baseQuery: fetchBaseQuery({
    baseUrl: "https://labpadre.craigmouser.com/api",
    //baseUrl: 'http://localhost:8000/api',
    prepareHeaders: async (headers, { getState }) => {
      const session = await Auth.currentSession();
      const authedUser = await Auth.currentAuthenticatedUser();
      const token = session.getAccessToken().getJwtToken();
      const auth = Buffer.from(`${authedUser.getUsername()}:password`).toString("base64");
      if (token) {
        // include token in req header
        headers.set("Authorization-JWT", `Bearer ${token}`);
        headers.set("authorization", `basic ${auth}`);
        return headers;
      }
    },
  }),
  tagTypes: [
    "PendingMessage",
    "ApprovedMessage",
    "SuperChats",
    "Members",
    "Streams",
  ],
  // The "endpoints" represent operations and requests for this server
  endpoints: (builder) => ({
    getPendingMessages: builder.query<IMessage[], any>({
      query: () => "/messages/pending",
      providesTags: ["PendingMessage"],
    }),
    acceptMessage: builder.mutation({
      query: (messageId) => ({
        url: `/messages/${messageId}?state=Accepted`,
        method: "POST",
      }),
      invalidatesTags: ["PendingMessage", "ApprovedMessage"],
    }),
    rejectMessage: builder.mutation({
      query: (messageId) => ({
        url: `/messages/${messageId}?state=Rejected`,
        method: "POST",
      }),
      invalidatesTags: ["PendingMessage"],
    }),
    dismissAllMessages: builder.mutation<void, void>({
      query: () => ({
        url: `/messages`,
        method: "DELETE",
      }),
      invalidatesTags: [
        "PendingMessage",
        "ApprovedMessage",
        "SuperChats",
        "Members",
      ],
    }),

    /* Streams */
    getStreams: builder.query<IStreamList, any>({
      query: () => "/streams",
      providesTags: ["Streams"],
    }),
    putStream: builder.mutation({
      query: (stream) => ({
        url: `/streams`,
        method: "PUT",
        body: stream,
      }),
      invalidatesTags: ["Streams"],
    }),
    stopStream: builder.mutation({
      query: (videoId) => ({
        url: `/streams/${videoId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Streams"],
    }),

    /* Commentary */
    getTextMessages: builder.query<IMessage[], any>({
      query: () => "/messages/textMessages",
      providesTags: ["ApprovedMessage"],
    }),
    getSuperChats: builder.query<IMessage[], any>({
      query: () => "/messages/superChats",
      providesTags: ["SuperChats"],
    }),
    getMembers: builder.query<IMessage[], any>({
      query: () => "/messages/members",
      providesTags: ["Members"],
    }),
    dismissMessage: builder.mutation({
      query: (messageId) => ({
        url: `/messages/${messageId}?state=Dismissed`,
        method: "POST",
      }),
      invalidatesTags: ["ApprovedMessage", "SuperChats", "Members"],
    }),
    publishMessage: builder.mutation({
      query: (messageId) => ({
        url: `/messages/publish/${messageId}`,
        method: "POST",
      }),
      invalidatesTags: ["ApprovedMessage", "SuperChats", "Members"],
    }),
    unpublishMessage: builder.mutation({
      query: (messageId) => ({
        url: `/messages/publish/${messageId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ApprovedMessage", "SuperChats", "Members"],
    }),
    unpublishAll: builder.mutation<void, void>({
      query: () => ({
        url: `/messages/publish`,
        method: "DELETE",
      }),
      invalidatesTags: ["ApprovedMessage", "SuperChats", "Members"],
    }),
    /* Layout */
    updateStreamLayout: builder.mutation({
      query: (streamLayout) => ({
        url: `/layout`,
        method: "POST",
        body: streamLayout,
      }),
    }),
    addWaitingLayout: builder.mutation({
      query: () => ({
        url: `/layout/waiting`,
        method: "PUT",
      }),
      invalidatesTags: ["Streams"],
    }),
    removeWaitingLayout: builder.mutation({
      query: () => ({
        url: `/layout/waiting`,
        method: "DELETE",
      }),
      invalidatesTags: ["Streams"],
    }),
  }),
});

// Export the auto-generated hook for the `getPosts` query endpoint
export const {
  useGetPendingMessagesQuery,
  useAcceptMessageMutation,
  useRejectMessageMutation,
  useGetStreamsQuery,
  usePutStreamMutation,
  useStopStreamMutation,
  useDismissAllMessagesMutation,
  useGetTextMessagesQuery,
  useGetSuperChatsQuery,
  useGetMembersQuery,
  useDismissMessageMutation,
  usePublishMessageMutation,
  useUnpublishMessageMutation,
  useUnpublishAllMutation,
  useUpdateStreamLayoutMutation,
  useAddWaitingLayoutMutation,
  useRemoveWaitingLayoutMutation,
} = apiSlice;
